import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { GlobalTradeSwitch } from "@/components/admin/GlobalTradeSwitch";
import { Button } from "@/components/ui/button";
import { LoadingSkeleton } from "@/components/LoadingSkeleton";
import { apiClient } from "@/lib/api";

const MetricCard = ({ title, value, testId }) => (
  <article className="rounded border border-slate-700 bg-slate-900 p-4" data-testid={`${testId}-card`}>
    <p className="text-xs uppercase tracking-wider text-slate-500" data-testid={`${testId}-label`}>{title}</p>
    <p className="mt-2 text-lg font-bold text-white" data-testid={`${testId}-value`}>{value ?? "-"}</p>
  </article>
);

export const AdminLiveTradingDashboardPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [scannerHealth, setScannerHealth] = useState(null);
  const [executionQuality, setExecutionQuality] = useState(null);
  const [riskSummary, setRiskSummary] = useState(null);
  const [dailyReport, setDailyReport] = useState(null);
  const [learningSummary, setLearningSummary] = useState(null);
  const [controlState, setControlState] = useState(null);
  const [actionAudit, setActionAudit] = useState([]);
  const [scannerControl, setScannerControl] = useState(null);
  const [loadError, setLoadError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const requests = await Promise.allSettled([
        apiClient.get("/admin/live-trading/summary", { params: { window: "1h" }, timeout: 12000 }),
        apiClient.get("/admin/live-trading/scanner-health", { params: { window: "1h" }, timeout: 12000 }),
        apiClient.get("/admin/live-trading/execution-quality", { params: { window: "1h" }, timeout: 12000 }),
        apiClient.get("/admin/live-trading/risk-summary", { params: { window: "1h" }, timeout: 12000 }),
        apiClient.get("/admin/live-trading/daily-report", { timeout: 12000 }),
        apiClient.get("/admin/live-trading/learning-summary", { params: { window: "24h" }, timeout: 12000 }),
        apiClient.get("/admin/live-trading/control-layer/state", { timeout: 12000 }),
        apiClient.get("/admin/live-trading/control-layer/action-audit", { params: { since_hours: 48, limit: 20 }, timeout: 12000 }),
        apiClient.get("/admin/live-trading/control-layer/scanner", { timeout: 12000 }),
      ]);

      const pick = (index) => (requests[index]?.status === "fulfilled" ? requests[index].value?.data : null);
      setSummary(pick(0));
      setScannerHealth(pick(1));
      setExecutionQuality(pick(2));
      setRiskSummary(pick(3));
      setDailyReport(pick(4));
      setLearningSummary(pick(5));
      setControlState(pick(6));
      setActionAudit(pick(7)?.items || []);
      setScannerControl(pick(8));

      const failed = requests.filter((item) => item.status === "rejected").length;
      if (failed > 0) {
        setLoadError(`${failed} panel verisi şu an alınamadı`);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return <LoadingSkeleton rows={8} testId="admin-live-trading-dashboard-loading-skeleton" />;
  }

  return (
    <section className="space-y-6" data-testid="admin-live-trading-dashboard-page">
      <header className="rounded border border-slate-700 bg-slate-950 p-5" data-testid="admin-live-trading-dashboard-header">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-slate-500" data-testid="admin-live-trading-dashboard-kicker">Live Observability</p>
            <h1 className="text-4xl font-black uppercase tracking-tight text-white" data-testid="admin-live-trading-dashboard-title">Readonly Live Monitor</h1>
            <p className="mt-2 max-w-3xl text-sm text-slate-400" data-testid="admin-live-trading-dashboard-subtitle">
              Bu ekran canlı sistemin özetini gösterir. Admin burada trade, scanner veya risk aksiyonu çalıştırmaz; yalnız görünürlük ve kill switch kullanır.
            </p>
          </div>
          <div className="flex flex-wrap gap-2" data-testid="admin-live-trading-dashboard-header-actions">
            <Button variant="outline" onClick={() => navigate("/admin/action-audit")} data-testid="admin-live-trading-dashboard-open-audit-button">Action Audit</Button>
            <Button onClick={load} data-testid="admin-live-trading-dashboard-refresh-button">Yenile</Button>
          </div>
        </div>
      </header>

      <section className="rounded border border-red-700/60 bg-red-950/20 p-5" data-testid="admin-live-trading-dashboard-kill-switch-panel">
        <p className="text-xs uppercase tracking-[0.2em] text-red-300" data-testid="admin-live-trading-dashboard-kill-switch-title">Emergency Kill Switch</p>
        <p className="mt-2 text-sm text-slate-300" data-testid="admin-live-trading-dashboard-kill-switch-description">
          Tek intentional kontrol budur. Bunun dışında admin tarafında trade akışını durduran bir buton veya işlem yüzeyi yoktur.
        </p>
        <div className="mt-4" data-testid="admin-live-trading-dashboard-kill-switch-control-wrap">
          <GlobalTradeSwitch />
        </div>
      </section>

      {loadError && (
        <div className="rounded border border-amber-700 bg-amber-950/20 p-4 text-sm text-amber-200" data-testid="admin-live-trading-dashboard-load-error-banner">
          {loadError}
        </div>
      )}

      <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4" data-testid="admin-live-trading-dashboard-summary-grid">
        <MetricCard title="Live Status" value={summary?.system_health?.live_status || summary?.window || "-"} testId="admin-live-trading-dashboard-live-status" />
        <MetricCard title="Critical Alerts" value={summary?.critical_alerts?.count ?? summary?.critical_alerts?.items?.length ?? "-"} testId="admin-live-trading-dashboard-critical-alerts" />
        <MetricCard title="Open Positions" value={controlState?.open_positions_count ?? "-"} testId="admin-live-trading-dashboard-open-positions" />
        <MetricCard title="Failed Orders" value={controlState?.failed_orders_count ?? "-"} testId="admin-live-trading-dashboard-failed-orders" />
      </section>

      <section className="grid gap-4 xl:grid-cols-3" data-testid="admin-live-trading-dashboard-observability-grid">
        <article className="rounded border border-slate-700 bg-slate-950 p-4" data-testid="admin-live-trading-dashboard-scanner-health-panel">
          <p className="text-xs uppercase tracking-[0.2em] text-cyan-300" data-testid="admin-live-trading-dashboard-scanner-health-title">Scanner Health</p>
          <pre className="mt-3 overflow-x-auto text-xs text-slate-300" data-testid="admin-live-trading-dashboard-scanner-health-json">{JSON.stringify(scannerHealth || {}, null, 2)}</pre>
        </article>

        <article className="rounded border border-slate-700 bg-slate-950 p-4" data-testid="admin-live-trading-dashboard-execution-quality-panel">
          <p className="text-xs uppercase tracking-[0.2em] text-emerald-300" data-testid="admin-live-trading-dashboard-execution-quality-title">Execution Quality</p>
          <pre className="mt-3 overflow-x-auto text-xs text-slate-300" data-testid="admin-live-trading-dashboard-execution-quality-json">{JSON.stringify(executionQuality || {}, null, 2)}</pre>
        </article>

        <article className="rounded border border-slate-700 bg-slate-950 p-4" data-testid="admin-live-trading-dashboard-risk-summary-panel">
          <p className="text-xs uppercase tracking-[0.2em] text-amber-300" data-testid="admin-live-trading-dashboard-risk-summary-title">Risk Summary</p>
          <pre className="mt-3 overflow-x-auto text-xs text-slate-300" data-testid="admin-live-trading-dashboard-risk-summary-json">{JSON.stringify(riskSummary || {}, null, 2)}</pre>
        </article>

        <article className="rounded border border-slate-700 bg-slate-950 p-4" data-testid="admin-live-trading-dashboard-daily-report-panel">
          <p className="text-xs uppercase tracking-[0.2em] text-violet-300" data-testid="admin-live-trading-dashboard-daily-report-title">Daily Report</p>
          <pre className="mt-3 overflow-x-auto text-xs text-slate-300" data-testid="admin-live-trading-dashboard-daily-report-json">{JSON.stringify(dailyReport || {}, null, 2)}</pre>
        </article>

        <article className="rounded border border-slate-700 bg-slate-950 p-4" data-testid="admin-live-trading-dashboard-learning-summary-panel">
          <p className="text-xs uppercase tracking-[0.2em] text-fuchsia-300" data-testid="admin-live-trading-dashboard-learning-summary-title">Learning Summary</p>
          <pre className="mt-3 overflow-x-auto text-xs text-slate-300" data-testid="admin-live-trading-dashboard-learning-summary-json">{JSON.stringify(learningSummary || {}, null, 2)}</pre>
        </article>

        <article className="rounded border border-slate-700 bg-slate-950 p-4" data-testid="admin-live-trading-dashboard-scanner-control-panel">
          <p className="text-xs uppercase tracking-[0.2em] text-sky-300" data-testid="admin-live-trading-dashboard-scanner-control-title">Scanner State</p>
          <pre className="mt-3 overflow-x-auto text-xs text-slate-300" data-testid="admin-live-trading-dashboard-scanner-control-json">{JSON.stringify(scannerControl || {}, null, 2)}</pre>
        </article>
      </section>

      <section className="rounded border border-slate-700 bg-slate-950 p-4" data-testid="admin-live-trading-dashboard-action-audit-panel">
        <div className="flex items-center justify-between gap-3">
          <p className="text-xs uppercase tracking-[0.2em] text-cyan-300" data-testid="admin-live-trading-dashboard-action-audit-title">Action Audit</p>
          <Button variant="outline" size="sm" onClick={() => navigate("/admin/action-audit")} data-testid="admin-live-trading-dashboard-action-audit-open-page-button">Detay</Button>
        </div>
        <div className="mt-3 space-y-2" data-testid="admin-live-trading-dashboard-action-audit-list">
          {actionAudit.length === 0 && <p className="text-sm text-slate-500" data-testid="admin-live-trading-dashboard-action-audit-empty">Kayıt yok.</p>}
          {actionAudit.map((item, index) => (
            <article key={`${item.id}-${index}`} className="rounded border border-slate-800 p-3 text-xs" data-testid={`admin-live-trading-dashboard-action-audit-item-${index}`}>
              <p className="font-semibold text-white" data-testid={`admin-live-trading-dashboard-action-audit-item-action-${index}`}>{item.action}</p>
              <p className="mt-1 text-slate-400" data-testid={`admin-live-trading-dashboard-action-audit-item-meta-${index}`}>{item.created_at} · {item.actor_role}</p>
            </article>
          ))}
        </div>
      </section>
    </section>
  );
};